import { Checkbox } from '@/components/forms/Checkbox';
import { cn } from '@/lib/cn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Node } from '@squaredup/graph';
import { SqUpPluginDisplayName } from '@squaredup/ids';
import clsx from 'clsx';
import Tooltip from 'components/tooltip/Tooltip';
import { TruncatedText } from 'components/TruncatedText';
import { getIconForNode, getTypeNameForNode } from 'lib/types';
import PluginIcon from 'pages/scope/PluginIcon';
import { MouseEvent } from 'react';

interface ObjectTableRowProps {
    object: Node;
    isActive?: boolean;
    /** Hides checkbox from table row to select objects */
    readOnly?: boolean;
    checkboxTooltipText?: React.ReactNode;
    className?: string;
    filterPropertiesColumns: string[];
    pluginLookup?: Map<string, { pluginName: string; sourceName: string }>;
    onClick?: (event: MouseEvent<HTMLTableRowElement>) => void;
}

interface ObjectOptionColumnProps {
    className?: string;
}

export const ObjectOptionColumn: React.FC<ObjectOptionColumnProps> = ({ children, className }) => (
    <span className={cn(className, 'flex items-center space-x-2')}>{children}</span>
);

const ObjectOptionCheckbox = ({
    id,
    checked,
    tooltipText,
    disabled
}: {
    id: string;
    checked: boolean;
    tooltipText?: React.ReactNode;
    disabled: boolean;
}) => {
    const checkbox = (
        <Checkbox id={id} checked={checked} className='block' disabled={disabled} aria-label='Select object' />
    );

    if (tooltipText) {
        return (
            <Tooltip title={tooltipText} disabled={!tooltipText}>
                {checkbox}
            </Tooltip>
        );
    }

    return checkbox;
};

export const ObjectTableRow: React.FC<ObjectTableRowProps> = ({
    object,
    isActive,
    readOnly,
    className,
    checkboxTooltipText,
    filterPropertiesColumns,
    pluginLookup,
    onClick,
    ...props
}) => {
    const { __configId, name, sourceType } = object;
    const { pluginName, sourceName } = pluginLookup?.get(__configId![0]) || {};

    const isSqUpObject = sourceType?.[0]?.includes('squaredup/');
    const disabled = !onClick;

    const tdClassName = 'pl-2 min-w-40 max-w-60 h-[25px]';

    return (
        <tr
            key={object.id}
            className={clsx(
                'text-left p-1 px-2',
                isActive ? 'font-bold text-textPrimary' : 'text-textSecondary',
                disabled || readOnly ? '' : 'cursor-pointer',
                className
            )}
            onClick={onClick}
            data-testid='objectOption'
            aria-disabled={disabled}
            {...props}
        >
            <td className={tdClassName}>
                <ObjectOptionColumn>
                    {!readOnly && (
                        <ObjectOptionCheckbox
                            id={`checkbox-${object.id}`}
                            checked={Boolean(isActive)}
                            tooltipText={checkboxTooltipText}
                            disabled={disabled}
                        />
                    )}
                    <TruncatedText title={name[0]} className='flex-1 min-w-0' />
                </ObjectOptionColumn>
            </td>

            {(sourceName || isSqUpObject) && (
                <td className={tdClassName}>
                    <ObjectOptionColumn>
                        <span className='flex-shrink-0 w-5 h-5'>
                            <PluginIcon pluginName={pluginName || SqUpPluginDisplayName} />
                        </span>
                        <TruncatedText title={sourceName || SqUpPluginDisplayName} className='flex-1 min-w-0' />
                    </ObjectOptionColumn>
                </td>
            )}

            <td className={tdClassName}>
                <ObjectOptionColumn className={cn(!filterPropertiesColumns?.length && 'col-span-2')}>
                    <span className='flex items-center justify-center w-5 h-5'>
                        <FontAwesomeIcon icon={getIconForNode(object)} className='flex-shrink-0' />
                    </span>
                    <TruncatedText
                        title={getTypeNameForNode(object, 'singular', true) || 'Unknown'}
                        className='flex-1 min-w-0'
                    />
                </ObjectOptionColumn>
            </td>

            {filterPropertiesColumns?.map((property: keyof typeof object, index) => {
                const propertyValue = Array.isArray(object[property])
                    ? object[property].join(', ') || ''
                    : String(object[property]?.[0] || '');

                return (
                    <td className={'pl-2 max-w-60 h-[25px]'}>
                        <ObjectOptionColumn
                            key={property}
                            className={cn(index === filterPropertiesColumns?.length - 1 && 'col-span-2')}
                        >
                            <TruncatedText title={propertyValue} className='flex-1 min-w-0' />
                        </ObjectOptionColumn>
                    </td>
                );
            })}

            {/* Empty column at the end for the property picker header */}
            <td className='w-0'></td>
        </tr>
    );
};
